import React, {
    useEffect, useRef, useState,
} from 'react';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import Title from 'components/UI/Title';
import Button from 'components/UI/Button';
import ReservationBasicInfo from 'components/BusinessLogic/Reservation/ReservationBasicInfo/Loadable';
import ContactUs from 'components/UI/ContactUs';
import ReservationNumbersSummaryCard from 'components/BusinessLogic/Reservation/ReservationNumbersSummary/Card';
import ReservationBundlesCard from 'components/BusinessLogic/Reservation/ReservationBundles/Card';
import ReservationTravelers from 'components/BusinessLogic/Reservation/ReservationTravelers/Card';
import Payments from 'components/BusinessLogic/Payments/Card';
import ReservationsAssistants from 'components/BusinessLogic/ReservationsAssistants/Card';
import { CONSTANTS } from 'services/constants';
import moment from 'moment';
import { connect } from 'react-redux';
import { loadReservation, reservationComponentIncludes } from 'store/dispatchers/reservations.dispatchers';
import _, { get, find, isEmpty } from 'lodash';
import { containsIncludes } from 'store/dispatchers/helpers';
import BookingDocuments from 'components/BusinessLogic/BookingDocuments/Card';
import ReservationDocuments from 'components/BusinessLogic/ReservationDocuments/Card';
import {
    buildMetaReservationSelector,
    buildReservationSelector,
} from 'store/selectors/reservations.selector';
import Skeleton from 'components/UI/Skeleton';

import { withRouteParams } from 'hoc/withRouteParams';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import ErrorMessage from 'components/UI/ErrorMessage';
import { formatStatus } from 'utils/formatRequestStatus';
import { Helmet } from 'react-helmet';

function ReservationPage({
    reservation, metaReservation, getReservation, params, isB2cPage,
})
{
    const conciergeEmail = getConciergeEmail(reservation);
    const agentEmail = getAgentEmail(reservation);
    const isLoading = metaReservation?.loading || false;
    const errorMessage = metaReservation?.error ?? '';
    const [pingState, setPingState] = useState({
        summarySection: false,
        bundlesSection: false,
        paymentsSection: false,
        documentsSection: false,
        assistantsSection: false,
    });
    let pingTimer = null;

    const summarySection = useRef(null);
    const paymentsSection = useRef(null);
    const documentsSection = useRef(null);
    const assistantsSection = useRef(null);

    function getAgentEmail(res)
    {
        const userEmails = get(res, 'request.user.userEmails', []);
        const defaultEmail = find(userEmails, { default: 1 });
        return get(defaultEmail, 'email');
    }

    function getConciergeEmail(res)
    {
        const conciergeGroups = get(res, 'request.user.conciergeGroups', []);

        return _.get(conciergeGroups, '[0].concierges[0].emails[0].email', null);
    }

    const loadDataIfNeeded = async () =>
    {
        if (
            metaReservation?.needUpdate === true
            || isEmpty(reservation)
            || !containsIncludes(reservationComponentIncludes, metaReservation?.loadedIncludes)
        )
        {
            getReservation(params.id);
        }
    };

    const scrollToSection = (elementRef, pingName) =>
    {
        setPingState({
            [pingName]: true,
        });

        if (pingTimer !== null)
        {
            clearTimeout(pingTimer);
        }

        window.scrollTo({
            top: elementRef.current.offsetTop - 30,
            behavior: 'smooth',
        });

        pingTimer = setTimeout(() => setPingState({}), 1500);
    };

    useEffect(() =>
    {
        loadDataIfNeeded();
    }, []);

    return (
        <div className="tw-flex tw-flex-col tw-flex-1 tw-w-full tw-p-10  lg:tw-pl-[248px] max-sm:tw-p-2 max-sm:tw-pt-10">
            <div className="tw-flex tw-gap-3 tw-bg-menu-background dark:tw-bg-menu-background-dark tw-px-5 tw-py-3 tw-rounded tw-mt-6 max-md:tw-flex-wrap max-sm:tw-hidden tw-sticky tw-top-0 tw-z-[750]">
                <Button className="tw-text-white tw-bg-transparent" onClick={() => scrollToSection(summarySection, 'summarySection')} margin="tw-m-0 max-md:tw-w-[30%]" type="custom">Booking Summary</Button>
                <Button className="tw-text-white tw-bg-transparent" onClick={() => scrollToSection(summarySection, 'bundlesSection')} margin="tw-m-0 max-md:tw-w-[30%]" type="custom">Packages</Button>
                <Button className="tw-text-white tw-bg-transparent" onClick={() => scrollToSection(paymentsSection, 'paymentsSection')} margin="tw-m-0 max-md:tw-w-[30%]" type="custom">Payments</Button>
                <Button className="tw-text-white tw-bg-transparent" onClick={() => scrollToSection(documentsSection, 'documentsSection')} margin="tw-m-0 max-md:tw-w-[30%]" type="custom">Documents</Button>
                {!isB2cPage && (<Button className="tw-text-white tw-bg-transparent" onClick={() => scrollToSection(assistantsSection, 'assistantsSection')} margin="tw-m-0 max-md:tw-w-[30%]" type="custom">Assistants</Button>)}
            </div>

            <div className="tw-flex tw-justify-between tw-items-center tw-flex-wrap tw-w-full">

                {
                    isLoading || !reservation ? (
                        <Skeleton type="title" className="tw-w-full" />
                    ) : (
                        <Title
                            title={`Booking #${reservation.booking_number} ${reservation.request?.name}`}
                            icon={faCalendar}
                        />
                    )
                }

            </div>

            <ErrorMessage errorMessage={errorMessage} />

            <div className="tw-grid tw-grid-cols-5 tw-gap-3 tw-mt-6">
                <div className="max-md:tw-col-span-4 max-sm:tw-col-span-5 tw-col-span-3 tw-grid tw-gap-3">
                    <div className="max-2xl:tw-max-w-[520px] max-sm:tw-max-w-full">
                        {
                            isLoading || !reservation ? (
                                <Skeleton type="card" />
                            ) : (
                                <ReservationBasicInfo
                                    agentEmail={agentEmail}
                                    fallback={<Skeleton type="card" />}
                                    bookingId={reservation.id}
                                    user={reservation.request.user}
                                    brand={reservation.request.branding}
                                    createdDate={moment(reservation.created_at ?? reservation.request.created_at).format(CONSTANTS.FORMATS.DATETIME_DATABASE)}
                                    closedDate={reservation.closed_date?.trim() ? moment(reservation.closed_date).format(CONSTANTS.FORMATS.DATE) : 'N/A'}
                                    status={formatStatus(reservation.status)}
                                    member={reservation.request.member}
                                    travelAgency={reservation.request.member?.travelAgency}
                                    bookingSecureId={reservation.secure_id}
                                    travelers={reservation.members}
                                    isB2cPage={isB2cPage}
                                />
                            )
                        }
                    </div>
                </div>
                <div className="max-md:tw-col-span-1 tw-col-span-2 tw-min-h-full">
                    <ContactUs className="tw-mr-0 tw-ml-auto tw-min-h-full" isB2cPage={isB2cPage} reference={reservation ? `Booking #${reservation.booking_number}` : null} />
                </div>
            </div>

            {reservation ? (
                <div ref={summarySection}>
                    <ReservationNumbersSummaryCard
                        pingState={pingState.summarySection}
                        reference={`Booking #${reservation.booking_number}`}
                        agentEmail={agentEmail}
                        conciergeEmail={conciergeEmail}
                        reservationComponents={{
                            Hotel: reservation.reservationHotels ?? [],
                            Flight: reservation.reservationFlights ?? [],
                            Insurance: reservation.reservationInsurances ?? [],
                            Service: reservation.reservationServices ?? [],
                        }}
                        isB2cPage={isB2cPage}
                        travelers={reservation.members}
                        getReservation={getReservation}
                        reservationId={reservation.id}
                    />
                </div>
            ) : (<Skeleton type="box" className="tw-w-full" />) }

            {reservation && reservation.reservationHotels ? (
                <div ref={summarySection}>
                    <ReservationBundlesCard
                        pingState={pingState.bundlesSection}
                        isB2cPage={isB2cPage}
                        reservationId={reservation.id}
                    />
                </div>
            ) : (<Skeleton type="box" className="tw-w-full" />) }

            {reservation && reservation.members ? (
                <div>
                    <ReservationTravelers
                        pingState={pingState.summarySection}
                        reservationId={reservation.id}
                    />
                </div>
            ) : (<Skeleton type="box" className="tw-w-full" />) }

            {!isLoading && reservation?.bundledInvoicePayments ? (
                <div ref={paymentsSection}>
                    <Payments
                        pingState={pingState.paymentsSection}
                        payments={reservation.bundledInvoicePayments.map((invoicePayment, index) => ({
                            id: `${index}-${invoicePayment.payment_type}`,
                            Type: invoicePayment.payment_type,
                            Item: invoicePayment.title,
                            Paid: invoicePayment.status === 'Paid' ? invoicePayment.amount : 0,
                            'Due/Payment Date': moment(invoicePayment.payment_date).format('MMM DD, YYYY'),
                            'Balance Due': invoicePayment.status === 'Due' ? invoicePayment.amount : 0,
                            currencyCode: invoicePayment.currency_code,
                        }))}
                    />
                </div>
            ) : (<Skeleton type="box" className="tw-w-full" />)}

            {reservation ? (<div ref={documentsSection}><ReservationDocuments pingState={pingState.documentsSection} reservationId={reservation.id} /></div>) : (<Skeleton type="box" className="tw-w-full" />) }
            {reservation ? (<BookingDocuments pingState={pingState.documentsSection} reservationId={reservation.id} />) : (<Skeleton type="box" className="tw-w-full" />) }
            {!isB2cPage && (reservation ? (<div ref={assistantsSection}><ReservationsAssistants reservationId={reservation.id} pingState={pingState.assistantsSection} /></div>) : (<Skeleton type="box" className="tw-w-full" />))}

            {reservation?.booking_number
            && (
                <Helmet title={`Booking #${reservation.booking_number} | ${isB2cPage ? 'Luxuria Vacations' : 'UJV'}`}>
                    <meta name="description" content={`Booking #${reservation.booking_number}`} />
                </Helmet>
            )}
        </div>
    );
}
ReservationPage.defaultProps = {
    isB2cPage: false,
};

ReservationPage.propTypes = {
    getReservation: PropTypes.func.isRequired,
    metaReservation: PropTypes.object,
    params: PropTypes.object.isRequired,
    reservation: PropTypes.object,
    isB2cPage: PropTypes.bool,
};

const mapStateToProps = () =>
{
    const reservationSelector = buildReservationSelector(['Booked']);
    const metaReservationSelector = buildMetaReservationSelector();

    return (state, props) =>
    {
        // IF WE DONT PARSEINT THE SELECTOR BREAKS
        const reservationId = parseInt(props.params.id, 10);

        return {
            reservation: reservationSelector(state, reservationId),
            metaReservation: metaReservationSelector(state, reservationId),
        };
    };
};

const mapDispatchToProps = (dispatch) => ({
    getReservation: (reservationId, includes) => dispatch(loadReservation(reservationId, includes)),
});

export default compose(
    withRouteParams,
    connect(mapStateToProps, mapDispatchToProps),
)(ReservationPage);
